import axios from "axios";
import Config from "../config";
import {retailerCatalogSearch, supplierCatalogSearch} from "../pages/catalogmanager/containers/catalog/catalog";
import {DependencyList, useEffect, useRef, useState} from "react";
import Auth from "../auth";
import moment from "moment";
import FileSaver from "filesaver.js-npm";
import Helper, {validateFlatFile} from "../helpers";
import cloneDeep from 'lodash/cloneDeep';
import {Properties} from "../pages/inventory/shared/feeds";
import {RunAttributeRules, ScopeAttributesToSet} from "../pages/inventory/shared/validation";

export const categoryFields = ["Id", "ParentId", "Name", "AttributeSet", "DestinationId", "BannerURL", "SyncStatus", "Level", "Children", "Path", "LastModified", "Hidden", "HiddenFromPartner", "Position", "CustomAttributes"];
export const attributeSetFields = ["Name", "FriendlyName", "Attributes", "Tags", "DestinationId", "SyncStatus", "System", "LastModified"];
export const attributeFields = ["Name", "FriendlyName", "Type", "Description", "Options", "Group", "Tags", "DestinationId", "SyncStatus", "System", "CustomAttributes", "HiddenFromPartner", "Hidden", "Editor", "ValidationRules", "LastModified"];
export const approvalStatusOptions = ["All", "New", "Approved", "Ignored", "Flagged", "Update Suggested", "Not Compliant", "Under Review"];
export const listingStatusOptions = ["All", "Listing Failed", "Not Listed", "Delist Pending", "Listing Pending", "Listed", "Delisted"];
export const messageStatusOptions = ["All", "New", "Complete", "In Progress", "Blocked"];

//region Feature Toggles

export const SUPPLIER_SYNDICATION_ENABLED = true;
export const AI_MAPPING_ENABLED = () => Auth.CompanyTags && Auth.CompanyTags.includes('demo');
export const AI_ASSIST_FEATURES = () => Auth.CompanyTags && Auth.CompanyTags.includes('demo');
//endregion

//region Helper Functions
export function isNullOrEmpty(value: unknown): boolean {
    return value === null || value === undefined || value === "";
}

export function unescape(string: string): string | null {
    return new DOMParser().parseFromString(string, 'text/html').querySelector('html')!.textContent;
}

export const getTickValues = (allTicks: any[], ticks: number): any[] => {
    var ret: any[] = [];
    if (!allTicks || ticks <= 0) {
        return ret;
    }
    var spacing = Math.max(Math.ceil(allTicks.length / ticks), 1);
    for (var i = 0; i < allTicks.length; i += spacing) {
        ret.push(allTicks[i]);
    }
    if (ret.indexOf(allTicks[allTicks.length - 1]) < 0) {
        ret.push(allTicks[allTicks.length - 1]);
    }
    return ret;
}

export const escapeSpaces = (field: string, headers: string[]): string => {
    if (field && headers.indexOf(field) > -1 && field.indexOf(" ") > -1) {
        return '{' + field + '}';
    }
    return field;
}

export function updateImportLinkTarget(link: string): string {
    let csvId = link.split("/").pop();
    let updatedLink = "";

    if (link.includes("https://stage.commerceapi.io/api/v1/attachments/catalogmanagerfiles/")) {
        updatedLink = `https://stageportal.logicbroker.com/storage/catalogmanagerfiles/${csvId}`
    } else if (link.includes("https://commerceapi.io/api/v1/attachments/catalogmanagerfiles/")) {
        updatedLink = `https://portal.logicbroker.com/storage/catalogmanagerfiles/${csvId}`
    }

    return updatedLink;
}

export function rtrim(str: string, chr?: string): string {
    if (!chr) {
        return str.replace(/\s+$/, '');
    }
    // Escape special regex characters
    const escaped = chr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rgxtrim = new RegExp(escaped + '+$');
    return str.replace(rgxtrim, '');
}

const getIsSmallScreen = (): boolean => window.innerWidth <= 1380;
export default function useIsSmallScreen(): boolean {
    const [isSmall, setIsSmall] = useState(getIsSmallScreen());

    useEffect(() => {
        const onResize = () => {
            setIsSmall(getIsSmallScreen());
        }

        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
        }
    }, []);

    return isSmall;
}

export function useDidUpdateEffect(fn: () => void | (() => void), inputs: DependencyList): void {
    const didMountRef = useRef(false);

    useEffect(() => {
        if (didMountRef.current) {
            return fn();
        }
        didMountRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, inputs);
}

export const approvalStatusColorMapper = (status: string): string => {
    switch (status) {
        case "New":
            return "#275e7A"
        case "Approved":
            return "green";
        case "Ignored":
            return "grey";
        case "Flagged":
            return "red";
        case "Update Suggested":
            return "orange";
        case "Not Compliant":
            return "red";
        case "Under Review":
            return "orange";
        default:
            return "orange";
    }
}
export const listingStatusColorMapper = (status: string): string => {
    switch (status) {
        case "Error":
            return "red"
        case "Not Listed":
            return "#275e7A";
        case "Delist Pending":
            return "orange";
        case "Listing Pending":
            return "orange";
        case "Listed":
            return "green";
        case "Delisted":
            return "grey";
        case "Listing Failed":
            return "red";
        default:
            return "orange";
    }
}
export const messageStatusColorMapper = (status: string): string => {
    switch (status) {
        case "New":
            return "rgb(39, 94, 122)";
        case "Complete":
            return "rgb(68, 128, 68)";
        case "In Progress":
            return "rgb(255, 165, 0)";
        case "Blocked":
            return "rgb(238, 50, 36)";
        default:
            return "grey";
    }

}

export const SyncStatusColorMapper = (status: string): string => {
    switch (status) {
        case "Synced":
            return "green"
        case "Not Synced":
            return "grey";
        default:
            return "grey";
    }
}
//endregion

//region Helper Components
export const useDebouncedEffect = (effect: () => void, deps: DependencyList, delay: number): void => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
}
//endregion

//region SupplierCatalog API Calls
export const approveProduct = async (productId: string, supplierId: number | string, supplierName: string, desiredSyncStatus: string | undefined, forceUpdate: boolean | undefined, merchantSku: string | undefined, merchantParentSku: string | undefined, retailerRules: {attributes: any[], attributeSetsBySupplier: Record<string, any[]>}): Promise<any> => {
    if (!Auth.hasPermission("inventory/manage")) {
        return {
            success: false,
            data: null,
            error: "You do not have permission to approve products."
        }
    }

    let encodedProductId = encodeURIComponent(productId);
    let encodedMerchantSku = encodeURIComponent(merchantSku || productId);
    try {
        const listStatus = desiredSyncStatus || "Listing Pending";
        let productResponse = await axios.get(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`);
        let productToApprove = productResponse.data;

        // Attribute sets are fetched per supplier because the retailer resolves a supplier product's
        // set through the partner-scoped endpoint, exactly as the product form does.
        const attributeSets = (retailerRules.attributeSetsBySupplier || {})[String(supplierId)] || [];
        const scopedAttributes = ScopeAttributesToSet(retailerRules.attributes, attributeSets, productToApprove.Attributes.attribute_set);
        if (scopedAttributes === null) {
            return {
                success: false,
                data: null,
                error: `Attribute set '${productToApprove.Attributes.attribute_set || ""}' could not be resolved, so this product could not be validated.`
            };
        }
        const ruleErrors = RunAttributeRules(scopedAttributes, productToApprove.Attributes);
        if (Object.keys(ruleErrors).length > 0) {
            return {
                success: false,
                data: null,
                error: Object.keys(ruleErrors).map((key) => ruleErrors[key]).join("\n")
            };
        }

        productToApprove.Attributes.approval_status = "Approved";
        productToApprove.Attributes.approved_by = Auth.getUsername() || "";
        productToApprove.Attributes.approval_date = moment.utc().toISOString(true);
        productToApprove.Attributes.supplier_id = supplierId;

        // Per ENG-414: only apply sync_status / listed_date / delisted_date to the
        // retailer-side copy. The supplier-side PUT below must not have its sync
        // state clobbered by the retailer's listStatus decision.
        const applyRetailerListingState = (retailerProduct: any) => {
            retailerProduct.Attributes.sync_status = listStatus;
            if (listStatus === "Delist Pending") {
                retailerProduct.Attributes.delisted_date = moment.utc().toISOString(true);
                retailerProduct.Attributes.delisted_by = Auth.getUsername();
            } else if (listStatus === "Listing Pending") {
                retailerProduct.Attributes.listed_date = moment.utc().toISOString(true);
                retailerProduct.Attributes.listed_by = Auth.getUsername();
            }
        };

        try {
            await axios.get(Config.api + `/api/v2/ProductOnboardingCenter/Products?id=${encodedMerchantSku}`);
            const newRetailerProduct = cloneDeep(productToApprove);
            if (merchantSku) newRetailerProduct.Attributes.sku = merchantSku;
            if (merchantParentSku) newRetailerProduct.Attributes.parent_sku = merchantParentSku;
            applyRetailerListingState(newRetailerProduct);
            if (forceUpdate) {
                await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/Products?id=${encodedMerchantSku}`, newRetailerProduct);
            } else {
                return {
                    success: false,
                    data: null,
                    error: "Product already exists in the catalog, please manually review."
                }
            }
        } catch (error) {
            if (error.response.status === 404) {
                const newRetailerProduct = cloneDeep(productToApprove);
                if (merchantSku) newRetailerProduct.Attributes.sku = merchantSku;
                if (merchantParentSku) newRetailerProduct.Attributes.parent_sku = merchantParentSku;
                applyRetailerListingState(newRetailerProduct);
                await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Products`, newRetailerProduct);
            } else {
                let errorMessage = error.message;
                if (error.response != null) errorMessage = error.response.data.Message;
                return {
                    success: false,
                    data: null,
                    error: errorMessage
                }
            }
        }

        // UPDATE SUPPLIER PRODUCT //
        productToApprove.Attributes.sku = productId;
        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`, productToApprove);

        return {
            success: true,
            data: null,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}
export const rejectProduct = async (productId: string, supplierId: number | string, supplierName: string, rejectMessage: string, product?: any): Promise<any> => {
    let encodedProductId = encodeURIComponent(productId);
    try {
        let productToReject = product;
        if (productToReject == null) {
            let productResponse = await axios.get(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`);
            productToReject = productResponse.data;
        }

        productToReject.ErrorMessage = rejectMessage;
        productToReject.Attributes.approval_status = "Not Compliant"

        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts?id=${encodedProductId}`, productToReject)
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error rejecting supplier product!",
            errorMessage: errorMessage
        };
    }
}
//endregion

//region RetailerCatalog API Calls
//endregion

//region General API Calls
export const utilTriggerWorkflowForPartner = async (partnerId: number | string): Promise<any> => {
    try {
        const response = await axios.get(Config.api + `/odata/company/Functions.RequestCatalogSync?partnerCoId=${partnerId}`);
        return {
            success: true,
            data: response.data,
            error: null
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilCheckSupplierSync = async (partnerCoId: number | string, type: string = 'product'): Promise<any> => {
    try {
        const response = await axios.get(Config.api + `/odata/Company/Functions.CheckSupplierSync?partnerCoId=${partnerCoId}&type=${type}`);
        return {
            success: true,
            data: response.data,
            error: null
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilRequestSupplierSync = async (partnerCoId: number | string, type: string = 'product'): Promise<any> => {
    try {
        const response = await axios.get(Config.api + `/odata/Company/Functions.RequestSupplierSync?partnerCoId=${partnerCoId}&type=${type}`);
        return {
            success: true,
            data: response.data,
            error: null
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilGetPartnerSuspensions = async (): Promise<any> => {
    try {
        const response = await axios.get(Config.api + `/api/v2/Partners/Suspensions`);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting partner suspensions!",
            errorMessage: errorMessage
        };
    }
}

export const utilCreatePartnerSuspension = async (partnerId: number | string, reasonCode: string, message: string, startDate: string, endDate: string): Promise<any> => {
    try {
        const body = {
            PartnerId: partnerId,
            ReasonCode: reasonCode,
            Message: message,
            StartDate: startDate,
            EndDate: endDate
        }

        await axios.post(Config.api + '/api/v2/Partners/Suspensions', body);
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating partner suspension!",
            errorMessage: errorMessage
        };
    }
}

export const utilDeletePartnerSuspension = async (partnerId: number | string): Promise<any> => {
    try {
        await axios.delete(Config.api + `/api/v2/Partners/Suspensions/${partnerId}`);
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting partner suspension!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetOrderStatusCounts = async (isRetailer: boolean, partnerId: number | string, startDate: string, endDate: string): Promise<any> => {
    try {
        const response = await axios.get(Config.api + `/odata/Company/Functions.GetOrderStatusCounts?$orderby=Date desc&isMerchant=${isRetailer}&partnerId=${partnerId}&startDate=${startDate}&endDate=${endDate}&timeZoneOffset=${new Date().getTimezoneOffset()}`);
        return response.data.value;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting order status counts!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetPartnerScorecards = async (startDate: string, endDate: string, includeBlanks: boolean): Promise<any> => {
    try {
        const response = await axios.get(Config.api + `/api/v2/Partners/Scorecards?startDate=${startDate}&endDate=${endDate}&includeBlank=${includeBlanks}`);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting partner scorecards!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetPartnerSettings = async (partnerId?: number | string): Promise<any> => {
    try {
        let url = Config.api + `/odata/Company/Functions.GetInventorySettings`;
        if (partnerId) {
            url += `?partnerId=${partnerId}`;
        }

        let configProperties = (await axios.get(url));
        let configData = configProperties.data;

        let settings: any = {
            autoMatch: configData[Properties.AutoMatch] === 'true',
            ignoreUnmatched: configData[Properties.IgnoreUnmatched] === 'true',
            caseSensitive: configData[Properties.CaseSensitive] === 'true',
            latestFeedOnly: configData[Properties.LatestFeedOnly] === 'true',
            allocation: 100,
            clearAfter: null,
            vendorNumber: '',
            forceVendorNumber: configData[Properties.ForceVendorNumber] === 'true',
        };

        let configuredAllocation = configData[Properties.Allocation];
        if (configuredAllocation) {
            settings.allocation = parseInt(String(parseFloat(configuredAllocation) * 100));
        }

        let clearAfter = configData[Properties.ClearAfter];
        if (clearAfter) {
            settings.clearAfter = clearAfter;
        }

        let vendorNumber = configData[Properties.VendorNumber];
        if (vendorNumber) {
            settings.vendorNumber = vendorNumber;
        }

        let minQty = configData[Properties.MinimumQuantity];
        if (minQty) {
            settings.minQty = parseInt(minQty);
        }

        return settings;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting partner settings!",
            errorMessage: errorMessage
        };
    }
}

//#region Inventory Management API Calls
export const utilSearchInventory = async (skus: string[], isRetailer: boolean = false): Promise<any> => {
    const skuField = isRetailer ? "merchantSku" : "supplierSku";
    const url = Config.api + "/odata/Company/Functions.ItemSearch"
        + `?$filter=${skus.map(sku => `(${skuField} eq '${encodeURIComponent((sku || "").replaceAll("'", "''"))}')`).join(" or ")}`;

    try {
        const response = await axios.get(url);
        return {
            data: response.data.value,
            error: null,
            success: true
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: null,
            error: errorMessage,
            success: false
        };
    }
}

export const utilSearchSupplierCatalog = async (skus: string[]): Promise<any> => {
    const url = Config.api + "/odata/Company/Functions.SupplierCatalogSearch"
        + `?$filter=${skus.map(sku => `(sku eq '${encodeURIComponent(sku)}')`).join(" or ")}`;

    try {
        const response = await axios.get(url);
        return {
            data: response.data.value,
            error: null,
            success: true
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: null,
            error: errorMessage,
            success: false
        };
    }
}

export const utilCheckInventory = async (partnerId: number | string, sku: string): Promise<any> => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/item?sku=${sku}`;
    try {
        const response = await axios.get(url);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error checking inventory!",
            errorMessage: errorMessage,
            errorStatus: error.response.status
        };
    }
}

export const utilCheckInventory2 = async (partnerId: number | string, sku: string): Promise<any> => {
    const url = Config.api + `/api/v2/inventory/${partnerId}/item?sku=${sku}`;
    try {
        const response = await axios.get(url);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error checking inventory!",
            errorMessage: errorMessage,
            errorStatus: error.response.status
        };
    }
}

export const utilDeleteAllInventory = async (partnerId: number | string): Promise<any> => {
    const url = Config.api + `/api/v2/inventory/${partnerId}`;
    try {
        await axios.delete(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilClearSkuMatches = async (partnerId: number | string): Promise<any> => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/matching`;
    try {
        await axios.delete(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error clearing SKU matches!",
            errorMessage: errorMessage
        };
    }
}

export const utilGetInventorySummary = async (partnerId: number | string): Promise<any> => {
    const url = Config.api + `/odata/Company/Functions.InventorySummary?partnerId=${partnerId}`;
    try {
        const response = await axios.get(url);
        const summary = response.data;
        let lastUpdate = '';
        if (summary.LastUpdate) {
            lastUpdate = moment(summary.LastUpdate).format("MM/DD/YYYY hh:mm:ss A");
        }
        return {
            lastUpdate: lastUpdate || ' ',
            lastUpdateCount: summary.LastUpdateCount === null ? ' ' : summary.LastUpdateCount
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting inventory summary!",
            errorMessage: errorMessage
        };
    }
}

export const utilZeroOutAllInventory = async (partnerId: number | string): Promise<any> => {
    const url = Config.api + `/api/v2/inventory/${partnerId}/ZeroOut`;
    try {
        await axios.put(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error zeroing out inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilResendInventory = async (partnerId: number | string): Promise<any> => {
    const url = Config.api + `/api/v2/inventory/${partnerId}/resend`;
    try {
        await axios.put(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error resending inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilDeleteInventory = async (partnerId: number | string, sku: string): Promise<any> => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/item?sku=${sku}`;
    try {
        await axios.delete(url);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilAddInventory = async (partnerId: number | string, merchantSku: string, supplierSku: string, existingInventory?: any): Promise<any> => {
    let values: any = {
        "SupplierId": partnerId,
        "SupplierSKU": supplierSku,
        "MerchantSKU": merchantSku,
    };

    if (existingInventory) {
        values = {...existingInventory, ...values}
    } else {
        values = {...values, "new": true}
    }

    const url = Config.api + `/api/v1/inventory/${partnerId}/item`;
    try {
        await axios.post(url, values);
        await new Promise(resolve => setTimeout(resolve, 750));
        return true;
    } catch (e) {
        var errors = ["Unexpected error occurred."];
        if (e.response) {
            errors = new Helper().getApiErrors(e.response.data);
        }
        return {
            open: true,
            errorHeader: "Error adding inventory!",
            errorMessage: errors.join(", ")
        };
    }
}

export const utilUpdateInventory = async (partnerId: number | string, inventory: any): Promise<any> => {
    const url = Config.api + `/api/v1/inventory/${partnerId}/item`;
    try {
        await axios.put(url, inventory);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilExportInventory = async (partnerId: number | string, format: string, matched: boolean, transform: boolean, modifiedAfter?: string, filter?: any): Promise<any> => {
    const requestUrl = partnerId === "all" 
        ? Config.api + '/api/v2/Inventory/All/Export'
        : Config.api + `/api/v2/Inventory/${partnerId}/Export`;
    const payload: any = {
        Mapped: matched,
        Transform: transform,
        IncludeNullQuantity: true,
        FileType: format
    };

    if (modifiedAfter) {
        payload.ModifiedAfter = modifiedAfter;
    }

    if (filter) {
        payload.Filter = filter;
    }

    try {
        let response = await axios.post(requestUrl, payload);
        let url = response.data.Body;
        let suffix = matched ? "mapped" : "unmapped";
        return {
            href: url,
            fileName: moment().format('YYYYMMDDHHmmss') + "-" + suffix + "." + format
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error exporting inventory!",
            errorMessage: errorMessage
        };
    }
}

// Group-scoped inventory export (LB-430).
// BACKEND DEPENDENCY (UNVERIFIED): posts a PartnerIds body array to the
// /All/Export endpoint to scope the export to the suppliers in the selected
// supplier group(s). There is no frontend evidence that /api/v2/Inventory/All/Export
// honours a PartnerIds filter; if the backend ignores it the export will return
// ALL suppliers (AC4 failure). This must be validated against staging / confirmed
// with the API team before AC4 can be declared complete. If a dedicated
// group-export endpoint is provided, update requestUrl accordingly.
export const utilExportInventoryByPartnerIds = async (partnerIds: (number | string)[], format: string, matched: boolean, transform: boolean, modifiedAfter?: string, filter?: any): Promise<any> => {
    const requestUrl = Config.api + '/api/v2/Inventory/All/Export';
    const payload: any = {
        Mapped: matched,
        Transform: transform,
        IncludeNullQuantity: true,
        FileType: format,
        PartnerIds: partnerIds
    };

    if (modifiedAfter) {
        payload.ModifiedAfter = modifiedAfter;
    }

    if (filter) {
        payload.Filter = filter;
    }

    try {
        let response = await axios.post(requestUrl, payload);
        let url = response.data.Body;
        let suffix = matched ? "mapped" : "unmapped";
        return {
            href: url,
            fileName: moment().format('YYYYMMDDHHmmss') + "-" + suffix + "." + format
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error exporting inventory!",
            errorMessage: errorMessage
        };
    }
}

export const utilImportInventory = async (partnerId: number | string, file: File, transform: boolean = false, fileType: string = 'standard', bypassValidation: boolean = false): Promise<any> => {
    let url = "";
    if(fileType === 'standard' || fileType === 'inventoryFeed'){
        url = Config.api + `/api/v2/inventory/${partnerId}?transform=false`;
    }else if(fileType === 'supplier' || fileType === 'customFeed'){
        url = Config.api + `/api/v2/inventory/${partnerId}`;
    }else if(fileType === 'matched' || fileType === 'matchedSkuFeed'){
        url = Config.api + `/api/v2/inventory/${partnerId}/matching`;
    }
    
    const formData = new FormData();
    formData.append("file", file);

    try {
        if (!bypassValidation) {
            let validationResult = await validateFlatFile(file, 'Inventory');
            if (validationResult) {
                return {
                open: true,
                errorHeader: "Validation Warning",
                errorMessage: validationResult
                };
            }
        }

        await axios.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error importing inventory!",
            errorMessage: errorMessage
        };
    }
}
//#endregion

export const updatePostStatus = async (postId: number | string, status: string): Promise<any> => {
    try {
        await axios.post(Config.api + '/odata/Company/Functions.UpdatePostStatus', {ThreadId: postId, Status: status});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error updating post status!",
            errorMessage: errorMessage
        };
    }
}
export const getThreadDetails = async (threadId: number | string): Promise<any> => {
    try {
        let response = await axios.get(Config.api + `/odata/Company/Functions.GetThreadDetails?threadId=${threadId}`);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting thread details!",
            errorMessage: errorMessage
        };
    }

}
export const getPosts = async (reference: string, category: string, partner: any, coid: any, isRetailer: boolean): Promise<any> => {
    const filter = `(Category eq '${encodeURIComponent(category)}' and Reference eq '${encodeURIComponent(reference)}')`;

    try {
        return (await axios.get(Config.api + `/odata/Company/VwCompanyPosts?$filter=${filter}&$orderby=Date desc`)).data.value;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting message posts!",
            errorMessage: errorMessage
        };
    }
}
export const getPostThreads = async (threadId: number | string, reference: string, partner: any, coid: any, isRetailer: boolean): Promise<any> => {
    // const contactCheck = isRetailer ? "" : `and Sender eq ${partner}`
    const filter = `(ThreadId eq ${threadId} and Reference eq '${reference}')`;

    try {
        let threads = (await axios.get(Config.api + `/odata/Company/VwCompanyPosts?$filter=${filter}&$orderby=Date desc`)).data.value;
        return threads;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting message threads!",
            errorMessage: errorMessage
        };
    }

}
export const createPost = async (requireAck: boolean, body: string, subject: string, status: string, reference: string, category: string, partner: any): Promise<any> => {
    try {
        await axios.post(Config.api + '/odata/Company/Functions.NewThread', {
            Body: body,
            Subject: subject,
            Reference: reference,
            Category: category,
            Recipients: [partner],
            RequireAck: requireAck,
            Status: status,
        });

        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error creating message post!",
            errorMessage: errorMessage
        };
    }
}
export const replyPost = async (replyTo: any, threadId: number | string, category: string, body: string, requireAck: boolean): Promise<any> => {
    try {
        await axios.post(Config.api + '/odata/Company/Functions.NewPost', {
            ReplyTo: replyTo,
            ThreadId: threadId,
            Category: category,
            Body: body,
            RequireAck: requireAck
        });
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error creating message post reply!",
            errorMessage: errorMessage
        };
    }
}

export const getRetailerCategoryTemplate = async (partnerId: number | string, category?: string[]): Promise<any> => {
    try {
        let request = {
            PartnerCoId: partnerId,
            Categories: category || []
        }
        const res = (await axios({
            url: Config.api + "/odata/Company/Functions.ProductTemplate",
            method: 'POST',
            responseType: 'blob',
            data: request
        })).data;
        const blob = new Blob([res], {type: "application/octet-stream"});
        (FileSaver as any).saveAs(blob, `product_template.xlsx`);
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting product template!",
            errorMessage: errorMessage
        };
    }
}
export const getAllTags = async (isPartner: boolean, isRetailer: boolean): Promise<any> => {
    let tags = [];
    let url = "";

    if (isRetailer) {
        url = retailerCatalogSearch;
        if (isPartner) url = supplierCatalogSearch;
    } else {
        url = supplierCatalogSearch;
    }

    url = url + `?$top=0&$count=true&facets=['tags,count:50']`;

    try {
        let response = await axios.get(url);
        tags = response.data["facets"]["tags"].map((value) => value.value);
        return tags;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting tags!",
            errorMessage: errorMessage
        };
    }
}
export const getAllCategories = async (partnerId: number | string, isRetailer: boolean): Promise<any> => {
    try {
        let url = "";
        if (isRetailer) {
            url = partnerId
                ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Categories`
                : Config.api + `/api/v2/ProductOnboardingCenter/Categories`;
        } else {
            url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Categories`;
        }

        const response = await axios.get(url);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting categories!",
            errorMessage: errorMessage
        };
    }
}
export const putProductStatus = async (productId: string, supplierId: number | string, status: string, isSupplier: boolean, updateMessage?: string): Promise<any> => {
    let encodedProductId = encodeURIComponent(productId);
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    if (isSupplier)
        url = Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/SupplierProducts`;

    try {
        let getResponse = await axios.get(url + `?id=${encodedProductId}`);
        let product = getResponse.data;

        product.Attributes.public_note = updateMessage;
        if (isSupplier)
            product.Attributes.approval_status = status;
        else {
            product.Attributes.sync_status = status;
            if (status === "Delist Pending") {
                product.Attributes.delisted_date = moment.utc().toISOString(true);
                product.Attributes.delisted_by = Auth.getUsername();
            } else if (status === "Listing Pending") {
                product.Attributes.listed_date = moment.utc().toISOString(true);
                product.Attributes.listed_by = Auth.getUsername();
            }
        }

        await axios.put(url + `?id=${encodedProductId}`, product);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating product status!",
            errorMessage: errorMessage
        };
    }
}
export const putProductSyncStatus = async (productId: string, partnerId: number | string, status: string, isSupplier: boolean): Promise<any> => {
    let encodedProductId = encodeURIComponent(productId);
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    if (isSupplier)
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;

    try {
        let getResponse = await axios.get(url + `?id=${encodedProductId}`);
        let product = getResponse.data;

        product.Attributes.sync_status = status;
        if (status === "Delist Pending") {
            product.Attributes.delisted_date = moment.utc().toISOString(true);
            product.Attributes.delisted_by = Auth.getUsername();
        } else if (status === "Listing Pending") {
            product.Attributes.listed_date = moment.utc().toISOString(true);
            product.Attributes.listed_by = Auth.getUsername();
        }

        await axios.put(url + `?id=${encodedProductId}`, product);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating product sync status!",
            errorMessage: errorMessage
        };
    }
}
export const createProduct = async (product: any, partnerId: number | string, isRetailer: boolean): Promise<any> => {
    let url = "";
    if (isRetailer) {
        url = partnerId
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    try {
        let response = await axios.post(url, {...product});
        return response.data.Id;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating product!",
            errorMessage: errorMessage
        };
    }
}
export const deleteProduct = async (productId: string, partnerId: number | string, isPartner: boolean, isRetailer: boolean): Promise<any> => {
    let encodedProductId = encodeURIComponent(productId);
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    try {
        await axios.delete(url + `?id=${encodedProductId}`)
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting products!",
            errorMessage: errorMessage
        };
    }
}
export const getProductCount = async (supplierId?: number | string): Promise<any> => {
    let url = retailerCatalogSearch + "?$top=0&$count=true";
    if (supplierId != null && supplierId !== "")
        url = supplierCatalogSearch + `?$top=0&$count=true&$filter=supplierId eq ${supplierId}`;

    try {
        let response = await axios.get(url);
        return response.data["@odata.count"] ?? 0;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting product count!",
            errorMessage: errorMessage
        };
    }
}
export const deleteAllProducts = async (): Promise<any> => {
    try {
        await axios.delete(Config.api + `/api/v3/Product/Products`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting products!",
            errorMessage: errorMessage
        };
    }
}
export const deleteAllSupplierProducts = async (partnerId: number | string): Promise<any> => {
    try {
        await axios.delete(Config.api + `/api/v3/Product/${encodeURIComponent(partnerId)}/SupplierProducts`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting supplier products!",
            errorMessage: errorMessage
        };
    }
}
export const updateProduct = async (product: any, productId: string, partnerId: number | string, isPartner: boolean, isRetailer: boolean): Promise<any> => {
    let encodedProductId = encodeURIComponent(productId);
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    product.ErrorMessage = null;

    try {
        let response = await axios.put(url + `?id=${encodedProductId}`, {...product});
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating product!",
            errorMessage: errorMessage
        };
    }
}
export const getProduct = async (productId: string, partnerId: number | string, isPartner: boolean, isRetailer: boolean): Promise<any> => {
    let encodedProductId = encodeURIComponent(productId);
    let url = "";
    if (isRetailer) {
        url = isPartner
            ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`
            : Config.api + `/api/v2/ProductOnboardingCenter/Products`;
    } else {
        url = isPartner
            ? url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Products`
            : url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/SupplierProducts`;
    }

    try {
        let response = await axios.get(url + `?id=${encodedProductId}`);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting product!",
            errorMessage: errorMessage,
            errorStatus: error.response != null ? error.response.status : 500
        };
    }
}
export const utilSearchRetailerCatalog = async (queryParams: string): Promise<any> => {
    const url = Config.api + "/odata/Company/Functions.RetailerCatalogSearch?" + queryParams;
    try {
        let response = await axios.get(url);
        return {
            data: response.data.value,
            code: response.status
        }
    } catch (error) {

        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: [],
            code: error?.response?.status ?? "500",
            error: errorMessage
        };
    }
}
//endregion

//region Settings API Calls
export const getCatalogSyncHistories = async (cancelSource?: any): Promise<any> => {
    try {
        const config = cancelSource ? {cancelToken: cancelSource.token} : {};
        let url = Config.api + `/odata/company/CatalogSyncHistories?$expand=LastEvent`;
        let response = await axios.get(url, config);

        return {
            data: response.data.value,
            code: response.status
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            data: [],
            code: error?.response?.status ?? "500",
            error: errorMessage
        }
    }
}
export const utilGetCatalogEvents = async (partnerId: number | string, cancelSource?: any): Promise<any> => {
    const url = Config.api + "/odata/Company/VwActivityEvents";
    const select = "?$select=EventDate,PartnerCoId,PartnerCompanyName,EventLevel,Summary,Details,Viewed,EventTypeId,Id,CustomerViewable";
    const other = "&$top=25&$count=true&$skip=0";
    const filter = `&$filter=(CustomerViewable eq true) and (EventTypeId eq 71) and (PartnerCoId eq ${partnerId})`;
    const order = "&$orderby=Id%20desc";

    const config = cancelSource ? {cancelToken: cancelSource.token} : {};

    try {
        const response = await axios.get(url + select + other + filter + order, config);

        if (response.data.value.length === 0) {
            return {
                success: true,
                data: "No events found",
                error: null
            }
        } else {
            return {
                success: true,
                data: null,
                error: null
            }
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: [],
            error: errorMessage
        }
    }
}
export const deleteAttribute = async (attributeName: string): Promise<any> => {
    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/Attributes/${attributeName}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error deleting attribute!",
            errorMessage: errorMessage
        };
    }
}

export const createAttribute = async (attribute: any): Promise<any> => {
    try {
        await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Attributes`, {...attribute});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating attribute!",
            errorMessage: errorMessage
        };
    }
}

export const updateAttribute = async (attribute: any, attributeId: string): Promise<any> => {
    // let encodedAttributeId = encodeURIComponent(attributeId);
    try {
        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/Attributes/${attributeId}`, {...attribute});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating attribute!",
            errorMessage: errorMessage
        };
    }
}

export const getAttributes = async (partnerId: number | string, isRetailer: boolean): Promise<any> => {
    // Don't need to worry about pagination, it might show currentPage and totalPages but it will always return all records.
    try {
        let url = isRetailer
            ? Config.api + `/api/v2/ProductOnboardingCenter/Attributes`
            : Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Attributes`;
        let response = await axios.get(url);
        return response.data.Records;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting attributes!",
            errorMessage: errorMessage
        };
    }
}

export const deleteCategory = async (categoryId: string): Promise<any> => {
    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/Categories/${categoryId}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error deleting category!",
            errorMessage: errorMessage
        };
    }
}

export const createCategory = async (category: any): Promise<any> => {
    try {
        await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Categories`, {...category});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error creating category!",
            errorMessage: errorMessage
        };
    }
}

export const updateCategory = async (category: any, categoryId: string): Promise<any> => {
    // let encodedCategoryId = encodeURIComponent(categoryId);
    try {
        await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/Categories/${categoryId}`, {...category});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error updating category!",
            errorMessage: errorMessage
        };
    }
}

export const deleteAttributeSet = async (attributeSetName: string): Promise<any> => {
    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets/${attributeSetName}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error deleting attribute set!",
            errorMessage: errorMessage
        };
    }
}

export const createAttributeSet = async (attributeSet: any): Promise<any> => {
    try {
        await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets`, {...attributeSet});
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error creating attribute set!",
            errorMessage: errorMessage
        };
    }
}

export const updateAttributeSet = async (attributeSet: any, attributeSetName: string): Promise<any> => {
    // let encodedAttributeSetId = encodeURIComponent(attributeSetId);
    try {
        let response = await axios.put(Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets/${attributeSetName}`, {...attributeSet});
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error updating attribute set!",
            errorMessage: errorMessage
        };
    }
}

export const getAttributeSets = async (isPartner: boolean, partnerId: number | string, cancelSource: any, isRetailer: boolean): Promise<any> => {
    try {
        let url = "";
        if (isRetailer) {
            url = isPartner ? Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/AttributeSets`
                : Config.api + `/api/v2/ProductOnboardingCenter/AttributeSets`
        } else {
            url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/AttributeSets`;
        }

        // Don't need to worry about pagination, it might show currentPage and totalPages but it will always return all records.
        let response = cancelSource != null
            ? await axios.get(url, {cancelToken: cancelSource.token})
            : await axios.get(url);
        let attributeSets = response.data.Records;

        // Data validation //
        let formattedAttributeSets = attributeSets.map((attributeSet) => {
            if (attributeSet.Attributes == null) {
                attributeSet.Attributes = [];
            }
            if (attributeSet.Name == null) {
                attributeSet.Name = "";
            }
            if (attributeSet.FriendlyName == null) {
                attributeSet.FriendlyName = "";
            }
            return attributeSet;
        });

        return formattedAttributeSets;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error getting attribute sets!",
            errorMessage: errorMessage
        };
    }
}

export const utilDeleteAll = async (type: string): Promise<any> => {
    if (type !== "Categories" && type !== "Attributes" && type !== "AttributeSets") {
        return {
            open: true,
            errorHeader: "Error deleting all items!",
            errorMessage: "Invalid type" + type
        };
    }

    try {
        await axios.delete(Config.api + `/api/v2/ProductOnboardingCenter/${type}`);
        return true;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error deleting all items!",
            errorMessage: errorMessage
        };
    }
}
//endregion

//region Export API Calls
export const utilGetPartners = async (): Promise<any> => {
    try {
        return (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting partners!",
            errorMessage: errorMessage
        };
    }
}
export const exportProducts = async (exportProfile: any, supplierId?: number | string): Promise<any> => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Exports/Products`;
    if (supplierId != null && supplierId !== "")
        url = Config.api + `/api/v2/ProductOnboardingCenter/${supplierId}/Exports/SupplierProducts`;

    try {
        let response = await axios.post(url, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting products!",
            errorMessage: errorMessage
        };
    }
}

export const importSupplierProducts = async (importProfile: any, partnerId: number | string): Promise<any> => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Imports/SupplierProducts`;

    try {
        let response = await axios.post(url, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing products!",
            errorMessage: errorMessage
        };
    }
}

export const importRetailerProducts = async (importProfile: any): Promise<any> => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Imports/Products`;

    try {
        let response = await axios.post(url, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing products!",
            errorMessage: errorMessage
        };
    }
}

export const importSupplierProductsStatus = async (partnerId: number | string, importId: string): Promise<any> => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/${partnerId}/Imports/SupplierProducts/Status?importId=${importId}`;

    try {
        let response = await axios.get(url);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting import status!",
            errorMessage: errorMessage
        };
    }
}

export const importRetailerProductsStatus = async (importId: string): Promise<any> => {
    let url = Config.api + `/api/v2/ProductOnboardingCenter/Imports/Products/Status?importId=${importId}`;

    try {
        let response = await axios.get(url);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error getting import status!",
            errorMessage: errorMessage
        };
    }
}

export const exportAttributes = async (exportProfile: any): Promise<any> => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Exports/Attributes`, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting attributes!",
            errorMessage: errorMessage
        };
    }
}

export const importAttributes = async (importProfile: any): Promise<any> => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Imports/Attributes`, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing attributes!",
            errorMessage: errorMessage
        };
    }
}

export const exportCategories = async (exportProfile: any): Promise<any> => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Exports/Categories`, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting categories!",
            errorMessage: errorMessage
        };
    }
}

export const importCategories = async (importProfile: any): Promise<any> => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Imports/Categories`, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error importing categories!",
            errorMessage: errorMessage
        };
    }
}

export const exportAttributeSets = async (exportProfile: any): Promise<any> => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Exports/AttributeSets`, exportProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            open: true,
            errorHeader: "Error exporting attribute sets!",
            errorMessage: errorMessage
        };
    }
}

export const importAttributeSets = async (importProfile: any): Promise<any> => {
    try {
        let response = await axios.post(Config.api + `/api/v2/ProductOnboardingCenter/Imports/AttributeSets`, importProfile);
        return response.data.Body;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            open: true,
            errorHeader: "Error importing attribute sets!",
            errorMessage: errorMessage
        };
    }
}

export const scrollToGroup = (id: string): void => {
    const group = document.getElementById(id)
    let offsetTop = group == null ? 0 : group.offsetTop
    window.scrollTo({
        top: offsetTop + -80,
        behavior: 'smooth'
    })
}
//endregion

//region Import API Calls
export const utilTestColumnMapping = async (records: any[], fields: any[], mappings: any[]): Promise<any> => {
    const url = Config.api + "/api/v2/Document/TestColumnMapping";
    const body = {
        Records: records,
        Fields: fields,
        Rules: mappings
    };

    try {
        let response = await axios.post(url, body);
        return {
            success: true,
            data: response.data.Records,
            error: null
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }


}

export const utilSearchLastImport = async (partnerId: number | string, signal?: AbortSignal): Promise<any> => {
    const selectQuery = "";//"$select=Id,CreateDate,CompanyName,PartnerCompanyName,PartnerCoId,Description,Type,Size,BlobPath";
    const filterQuery = `$filter=(Type eq 'product' and Duplicate eq false and PartnerCoId eq ${partnerId})`;
    const otherQuery = `$top=1&$orderby=Id desc`;
    const url = Config.api + "/odata/Company/Attachments?" + selectQuery + "&" + filterQuery + "&" + otherQuery;

    try {
        let response = await axios.get(url, {
            signal: signal
        } as any);
        return {
            success: true,
            data: response.data.value,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}

export const utilSearchLastImports = async (top: number = 10, signal?: AbortSignal): Promise<any> => {
    const selectQuery = "";//"$select=Id,CreateDate,CompanyName,PartnerCompanyName,PartnerCoId,Description,Type,Size,BlobPath";
    const filterQuery = `$filter=(Type eq 'product' and Duplicate eq false)`;
    const otherQuery = `$top=${top}&$orderby=Id desc`;
    const url = Config.api + "/odata/Company/Attachments?" + selectQuery + "&" + filterQuery + "&" + otherQuery;

    try {
        let response = await axios.get(url, { signal } as any);
        return {
            success: true,
            data: response.data.value,
            error: null
        };
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;
        return {
            success: false,
            data: null,
            error: errorMessage
        }
    }
}
//endregion

//region Attachments API Calls
export const createProductBackupCsv = async (selectedProducts: any[], selectedAttributes: any[], isRetailer: boolean, currentCatalog: string): Promise<string> => {
    try {
        const backupData = [];
        const isEditingSupplierCatalog = (currentCatalog === 'partnercatalog' && isRetailer) || 
                                         (currentCatalog !== 'partnercatalog' && !isRetailer);
        
        const headers = isEditingSupplierCatalog
            ? ['SKU', 'Supplier ID', ...selectedAttributes.map(attr => attr.FriendlyName || attr.Name)]
            : ['SKU', ...selectedAttributes.map(attr => attr.FriendlyName || attr.Name)];
        
        backupData.push(headers);
        
        for (const product of selectedProducts) {
            try {
                const partnerId = isRetailer ? product.supplierId : product.merchantId;
                const isPartner = currentCatalog === "partnercatalog";
                
                const fullProductData = await getProduct(product.sku, partnerId, isPartner, isRetailer);
                
                if (!fullProductData.errorMessage) {
                    const row = [product.sku];
                    
                    if (isEditingSupplierCatalog) {
                        const supplierIdValue = isRetailer ? (product.supplierId || '') : (partnerId || '');
                        row.push(supplierIdValue);
                    }
                    
                    selectedAttributes.forEach(attribute => {
                        const currentValue = fullProductData.Attributes?.[attribute.Name];
                        let formattedValue = '';
                        
                        if (currentValue !== null && currentValue !== undefined) {
                            if (Array.isArray(currentValue)) {
                                formattedValue = currentValue.join(', ');
                            } else if (typeof currentValue === 'boolean') {
                                formattedValue = currentValue ? 'Yes' : 'No';
                            } else {
                                formattedValue = String(currentValue);
                            }
                        }
                        
                        row.push(formattedValue);
                    });
                    
                    backupData.push(row);
                }
            } catch (error) {
                console.error(`Error fetching data for ${product.sku}:`, error);

                // Add row with SKU and empty values for failed products
                const partnerId = isRetailer ? product.supplierId : product.merchantId;
                const supplierIdValue = isRetailer ? (product.supplierId || 'ERROR') : (partnerId || 'ERROR');
                const row = isEditingSupplierCatalog
                    ? [product.sku, supplierIdValue, ...selectedAttributes.map(() => 'ERROR_FETCHING_DATA')]
                    : [product.sku, ...selectedAttributes.map(() => 'ERROR_FETCHING_DATA')];
                backupData.push(row);
            }
        }
        
        const csvContent = backupData.map(row => 
            row.map(cell => {
                // Escape quotes and wrap in quotes if contains comma, quote, or newline
                const cellStr = String(cell || '');
                if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                    return '"' + cellStr.replace(/"/g, '""') + '"';
                }
                return cellStr;
            }).join(',')
        ).join('\n');
        
        return csvContent;
    } catch (error) {
        console.error('Error creating backup CSV:', error);
        throw error;
    }
};

export const uploadBackupToAttachments = async (csvContent: string, filename: string, isRetailer: boolean, currentCatalog: string): Promise<any> => {
    try {
        const blob = new Blob([csvContent], { type: 'text/csv' });
        
        const formData = new FormData();
        formData.append('file', blob, filename);
        
        const type = 'poc_product_backup.csv';
        let catalogType;
        if (currentCatalog === 'partnercatalog') {
            catalogType = isRetailer ? 'Supplier Catalog' : 'Retailer Catalog';
        } else {
            catalogType = isRetailer ? 'Retailer Catalog' : 'Supplier Catalog';
        }
        const description = `${catalogType} Product Backup for Bulk Update`;
        
        const response = await axios.post(Config.api + `/api/v2/Attachments?Type=${encodeURIComponent(type)}&Description=${encodeURIComponent(description)}`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
        
        return {
            success: true,
            data: response.data,
            filename: filename
        };
    } catch (error) {
        console.error('Error uploading backup to attachments:', error);
        let errorMessage = error.message;
        if (error.response?.data?.Message) {
            errorMessage = error.response.data.Message;
        }
        return {
            success: false,
            error: errorMessage
        };
    }
};

export const createAndUploadProductBackup = async (selectedProducts: any[], selectedAttributes: any[], isRetailer: boolean, currentCatalog: string): Promise<any> => {
    try {
        const csvContent = await createProductBackupCsv(selectedProducts, selectedAttributes, isRetailer, currentCatalog);
        
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
        let catalogType;
        if (currentCatalog === 'partnercatalog') {
            catalogType = isRetailer ? 'supplier' : 'retailer';
        } else {
            catalogType = isRetailer ? 'retailer' : 'supplier';
        }
        const filename = `product_backup_${catalogType}_${timestamp}.csv`;
        
        const uploadResult = await uploadBackupToAttachments(csvContent, filename, isRetailer, currentCatalog);
        
        if (uploadResult.success) {
            return {
                success: true,
                data: uploadResult.data,
                filename: uploadResult.filename,
                message: `Backup created successfully: ${uploadResult.filename}`
            };
        } else {
            return {
                success: false,
                error: uploadResult.error
            };
        }
    } catch (error) {
        console.error('Error creating and uploading product backup:', error);
        return {
            success: false,
            error: error.message
        };
    }
};
//endregion

//region Supplier Groups API Calls
export const utilGetSupplierGroups = async (): Promise<any> => {
    try {
        const response = await axios.get(Config.api + '/api/v3/Partners/SupplierGroups');
        // Handle paginated response format
        const data = response.data;
        if (Array.isArray(data)) {
            return data;
        } else if (data && Array.isArray(data.Records)) {
            return data.Records;
        } else if (data && Array.isArray(data.Body)) {
            return data.Body;
        } else {
            console.warn('Unexpected supplier groups response format:', data);
            return [];
        }
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            errorMessage: errorMessage
        };
    }
}

export const utilCreateSupplierGroup = async (name: string, partnerIds: (number | string)[]): Promise<any> => {
    try {
        const payload = {
            Name: name,
            Partners: partnerIds.map(id => ({ Id: id }))
        };
        const response = await axios.post(Config.api + '/api/v3/Partners/SupplierGroups', payload);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            errorMessage: errorMessage
        };
    }
}

export const utilUpdateSupplierGroup = async (groupId: string, name: string | undefined, partnerIds: (number | string)[]): Promise<any> => {
    try {
        const payload: any = {
            Partners: partnerIds.map(id => ({ Id: id }))
        };
        if (name) {
            payload.Name = name;
        }
        // groupId could be a name, so we need to encode it
        const encodedGroupId = encodeURIComponent(groupId);
        
        const response = await axios.put(Config.api + `/api/v3/Partners/SupplierGroups/${encodedGroupId}`, payload);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            errorMessage: errorMessage
        };
    }
}

export const utilDeleteSupplierGroup = async (groupId: string): Promise<any> => {
    try {
        // groupId could be a name, so we need to encode it
        const encodedGroupId = encodeURIComponent(groupId);
        const response = await axios.delete(Config.api + `/api/v3/Partners/SupplierGroups/${encodedGroupId}`);
        return response.data;
    } catch (error) {
        let errorMessage = error.message;
        if (error.response != null) errorMessage = error.response.data.Message;

        return {
            errorMessage: errorMessage
        };
    }
}
//endregion
